import { Tooltip } from "@mui/material";
import { TabelaVirtualizadaGenerica } from "components/TabelaVirtualizadaGenerica/TabelaVirtualizadaGenerica.tsx";
import MotionSemAutorizacao from "core/components/MotionSemAutorizacao.tsx";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChartsHandler from "../../../../components/Charts/ChartsHandler.tsx";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent.tsx";
import { useProfile } from "../../../../contexts/ProfileContext.tsx";
import { useToast } from "../../../../contexts/ToastContext.tsx";
import Button from "../../../../core/components/Button.tsx";
import Icon from "../../../../core/components/Icon.tsx";
import IconButton from "../../../../core/components/IconButton.tsx";
import MotionContainer from "../../../../core/components/MotionContainer.tsx";
import Separator from "../../../../core/components/Separator.tsx";
import FiltroData from "../../../../core/data-entry/FiltroData.tsx";
import Loader from "../../../../core/feedback/Loader.tsx";
import Layout from "../../../../core/layout/Layout.tsx";
import {
	isDataDoisMil,
	primeiroDiaDosMesFormatado,
} from "../../../../core/utils/UtilityDates.ts";
import useService from "../../../../hooks/useService.ts";
import {
	getIndicadorFaturamentoMensal,
	postIndicadorFaturamentoMensalResumo,
} from "../../../../services/ModuloGerenciador/Dashboard.js";
import {
	IndicadorFaturamentoMensal as IndicadorFaturamentoMensalModel,
	IndicadorFaturamentoMensalTabelaUnidade,
	getColunasTabelaClientes,
	getColunasTabelaSeries,
	getColunasTabelaUnidades,
} from "./Indicador.interface.ts";
import "./IndicadorFaturamentoMensal.css";
import { anoAnterior, anoAtual, mesAtual } from "./IndicadorUtil.tsx";

enum StateKey {
	LOADING = "loading",
	RESULTS = "results",
}

const getHojeLocalFormatado = () => {
	const hoje = new Date();
	const ano = hoje.getFullYear();
	const mes = String(hoje.getMonth() + 1).padStart(2, "0");
	const dia = String(hoje.getDate()).padStart(2, "0");
	return `${ano}-${mes}-${dia}`;
};

const parseLocalDate = (value: string) => {
	if (!value || typeof value !== "string") {
		return new Date(NaN);
	}
	const parts = value.split("-");
	if (parts.length !== 3) {
		return new Date(NaN);
	}
	const [year, month, day] = parts.map(Number);
	if (!year || !month || !day) {
		return new Date(NaN);
	}
	return new Date(year, month - 1, day);
};

const getResponseData = (error: unknown) => {
	if (!error || typeof error !== "object") {
		return undefined;
	}
	if (!("response" in error)) {
		return undefined;
	}
	const response = (error as { response?: { data?: unknown } }).response;
	return response?.data;
};

function IndicadorFaturamentoMensal() {
	const [tabelaAnteriorLabel, setTabelaAnteriorLabel] = useState("");
	const [tabelaAtualLabel, setTabelaAtualLabel] = useState("");
	const [faturamentoComparativoLabel, setFaturamentoComparativoLabel] =
		useState("");
	const [dadosGrafico1, setDadosGrafico1] =
		useState<IndicadorFaturamentoMensalModel>();
	const [semAutorizacao, setSemAutorizacao] = useState(false);
	const [datasPesquisa, setDatasPesquisa] = useState<{
		inicio: string;
		fim: string;
	}>(() => ({
		inicio: primeiroDiaDosMesFormatado,
		fim: getHojeLocalFormatado(),
	}));
	const [ultimaPesquisa, setUltimaPesquisa] = useState<{
		inicio: string;
		fim: string;
	}>(() => ({
		inicio: primeiroDiaDosMesFormatado,
		fim: getHojeLocalFormatado(),
	}));
	const [viewState, setViewState] = useState<StateKey>(StateKey.LOADING);
	const [summaryState, setSummaryState] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");
	const [summaryText, setSummaryText] = useState("");
	const [summaryInsights, setSummaryInsights] = useState<string[]>([]);
	const [summaryErrorMessage, setSummaryErrorMessage] = useState("");

	const hasBuscadoDados = useRef(false);
	const hasAlteradoDatas = useRef(false);
	const timeoutsRef = useRef<number[]>([]);

	const scheduleTimeout = useCallback((fn: () => void, delay = 250) => {
		const id = window.setTimeout(fn, delay);
		timeoutsRef.current.push(id);
		return id;
	}, []);

	const clearPendingTimeouts = useCallback(() => {
		timeoutsRef.current.forEach((id) => clearTimeout(id));
		timeoutsRef.current = [];
	}, []);

	const { profile, hasRegra } = useProfile();

	const { showToast } = useToast();

	const [isLoadingDados, buscarIndicadorFaturamentoMensal] = useService(
		getIndicadorFaturamentoMensal,
		{
			autoStart: false,
			onData: (result) => {
				scheduleTimeout(() => {
					setViewState(StateKey.RESULTS);

					setDadosGrafico1((prevData) => {
						if (!result.data) return prevData;

						const tabelaUnidades = result.data.tabelaUnidades || [];
						const outrasUnidades = tabelaUnidades.filter(
							(item: IndicadorFaturamentoMensalTabelaUnidade) =>
								item.unidade !== "Total",
						);
						const totalUnidade = tabelaUnidades.find(
							(item: IndicadorFaturamentoMensalTabelaUnidade) =>
								item.unidade === "Total",
						);

						return {
							...result.data,
							tabelaUnidades: totalUnidade
								? [...outrasUnidades, totalUnidade]
								: outrasUnidades,
						};
					});
				}, 300);
			},
			onError: (error: unknown) => {
				hasBuscadoDados.current = false;
				const responseData = getResponseData(error);
				if (typeof responseData === "string") {
					showToast(responseData, "error");
				} else {
					showToast(
						"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte.",
						"error",
					);
				}
			},
		},
	);

	const getValidationMessage = useCallback(() => {
		const dataInicio = parseLocalDate(datasPesquisa.inicio);
		const dataFim = parseLocalDate(datasPesquisa.fim);
		const hoje = new Date();

		if (Number.isNaN(dataInicio.getTime()) || Number.isNaN(dataFim.getTime())) {
			return "Selecione uma data válida.";
		}

		if (dataInicio > hoje) {
			return "A data de início não pode estar no futuro.";
		}

		if (dataInicio > dataFim) {
			return "A data de início não pode ser maior que a data de fim.";
		}

		if (
			isDataDoisMil(datasPesquisa.inicio) ||
			isDataDoisMil(datasPesquisa.fim) ||
			dataInicio.getFullYear() < 2000 ||
			dataFim.getFullYear() < 2000
		) {
			return "Ano inválido. Por favor, selecione uma data válida.";
		}

		return "";
	}, [datasPesquisa]);

	const datasValidadas = useCallback(() => {
		const message = getValidationMessage();
		if (message) {
			showToast(message, "error");
			return false;
		}

		return true;
	}, [getValidationMessage, showToast]);

	const handleGerarResumoIa = useCallback(async () => {
		const validationMessage = getValidationMessage();
		if (validationMessage) {
			setSummaryState("error");
			setSummaryText("");
			setSummaryInsights([]);
			setSummaryErrorMessage(validationMessage);
			showToast(validationMessage, "error");
			return;
		}

		if (!profile?.token) {
			return;
		}

		setSummaryState("loading");
		setSummaryErrorMessage("");

		try {
			const result = await postIndicadorFaturamentoMensalResumo({
				token: profile.token,
				dataInicio: datasPesquisa.inicio,
				dataFim: datasPesquisa.fim,
			});

			const resumo = result?.data?.resumo ?? "";
			const insights = result?.data?.insights;

			setSummaryText(resumo);
			setSummaryInsights(Array.isArray(insights) ? insights : []);
			setSummaryState("success");
		} catch (error: unknown) {
			const responseData = getResponseData(error);
			const message =
				typeof responseData === "string"
					? responseData
					: typeof responseData === "object" && responseData !== null
						? (() => {
								const data = responseData as {
									mensagem?: unknown;
									message?: unknown;
								};
								if (typeof data.mensagem === "string") {
									return data.mensagem;
								}
								if (typeof data.message === "string") {
									return data.message;
								}
								return "Não foi possível gerar o resumo.";
							})()
						: "Não foi possível gerar o resumo.";
			setSummaryState("error");
			setSummaryText("");
			setSummaryInsights([]);
			setSummaryErrorMessage(message);
			showToast(message, "error");
		}
	}, [
		getValidationMessage,
		datasPesquisa.fim,
		datasPesquisa.inicio,
		profile?.token,
		showToast,
	]);

	const buscarDadosFiltrados = useCallback(async () => {
		if (!datasValidadas()) {
			return;
		}

		setUltimaPesquisa({ inicio: datasPesquisa.inicio, fim: datasPesquisa.fim });
		setSummaryState("idle");
		setSummaryText("");
		setSummaryInsights([]);
		setSummaryErrorMessage("");
		setViewState(StateKey.LOADING);

		const formatter = new Intl.DateTimeFormat("pt-BR", {
			month: "long",
			year: "numeric",
		});
		const dataAtual = parseLocalDate(datasPesquisa.fim);
		const mesAnoAtual =
			formatter.format(dataAtual).charAt(0).toUpperCase() +
			formatter.format(dataAtual).slice(1);
		const dataAnoAnterior = new Date(dataAtual);
		dataAnoAnterior.setFullYear(dataAnoAnterior.getFullYear() - 1);
		const mesAnoAnterior =
			formatter.format(dataAnoAnterior).charAt(0).toUpperCase() +
			formatter.format(dataAnoAnterior).slice(1);

		setFaturamentoComparativoLabel(`${mesAnoAnterior} x ${mesAnoAtual}`);
		setTabelaAnteriorLabel(mesAnoAnterior);
		setTabelaAtualLabel(mesAnoAtual);

		buscarIndicadorFaturamentoMensal({
			token: profile!.token,
			dataInicio: datasPesquisa.inicio,
			dataFim: datasPesquisa.fim,
		});
	}, [
		profile!.token,
		datasPesquisa,
		buscarIndicadorFaturamentoMensal,
		datasValidadas,
	]);

	const buscarDadosInicial = useCallback(async () => {
		setSummaryState("idle");
		setSummaryText("");
		setSummaryInsights([]);
		setSummaryErrorMessage("");
		setViewState(StateKey.LOADING);
		setFaturamentoComparativoLabel(
			`${mesAtual}/${anoAnterior} x ${mesAtual}/${anoAtual}`,
		);
		setTabelaAnteriorLabel(`${mesAtual}/${anoAnterior}`);
		setTabelaAtualLabel(`${mesAtual}/${anoAtual}`);
		setUltimaPesquisa({
			inicio: primeiroDiaDosMesFormatado,
			fim: getHojeLocalFormatado(),
		});

		buscarIndicadorFaturamentoMensal({
			token: profile!.token,
			dataInicio: primeiroDiaDosMesFormatado,
			dataFim: getHojeLocalFormatado(),
		});
	}, [profile!.token, buscarIndicadorFaturamentoMensal]);

	const handleClearSearch = useCallback(() => {
		setDatasPesquisa({
			inicio: primeiroDiaDosMesFormatado,
			fim: getHojeLocalFormatado(),
		});
		buscarDadosInicial();
		clearPendingTimeouts();
	}, [buscarDadosInicial]);

	useEffect(() => {
		if (profile?.token && !hasBuscadoDados.current) {
			setSemAutorizacao(!hasRegra("Indicador Faturamento Mensal"));
			buscarDadosInicial();
			hasBuscadoDados.current = true;
		}
	}, [profile?.token, hasRegra, buscarDadosInicial]);

	useEffect(() => {
		if (isLoadingDados) {
			scheduleTimeout(() => {
				setViewState(StateKey.LOADING);
			}, 300);
		}
	}, [isLoadingDados]);

	useEffect(() => {
		if (!hasAlteradoDatas.current) {
			hasAlteradoDatas.current = true;
			return;
		}

		setSummaryState("idle");
		setSummaryText("");
		setSummaryInsights([]);
		setSummaryErrorMessage("");
	}, [datasPesquisa.inicio, datasPesquisa.fim]);

	const colunasSeries = useMemo(() => {
		return dadosGrafico1?.categories
			? getColunasTabelaSeries(dadosGrafico1.categories)
			: [];
	}, [dadosGrafico1?.categories]);

	const isLoading = useMemo(() => {
		return (
			isLoadingDados ||
			!profile?.token ||
			(!dadosGrafico1 && !hasBuscadoDados.current)
		);
	}, [isLoadingDados, dadosGrafico1, profile?.token]);

	const isSummaryLoading = useMemo(
		() => summaryState === "loading",
		[summaryState],
	);

	const isPesquisaAtual = useMemo(() => {
		return (
			ultimaPesquisa.inicio === datasPesquisa.inicio &&
			ultimaPesquisa.fim === datasPesquisa.fim
		);
	}, [ultimaPesquisa, datasPesquisa]);

	const resumoTooltip = useMemo(() => {
		if (summaryState === "loading") {
			return "Gerando resumo...";
		}
		if (!isPesquisaAtual) {
			return "Clique na lupa para atualizar a pesquisa.";
		}
		return "Gerar resumo I.A";
	}, [summaryState, isPesquisaAtual]);

	const highlightTerms = useMemo(() => {
		const terms = new Set<string>();
		(dadosGrafico1?.tabelaUnidades || []).forEach((item) => {
			if (item?.unidade && item.unidade !== "Total") {
				terms.add(item.unidade);
			}
		});
		(dadosGrafico1?.tabelaClientes || []).forEach((item) => {
			if (item?.cliente) {
				terms.add(item.cliente);
			}
		});
		return Array.from(terms)
			.filter(Boolean)
			.sort((a, b) => b.length - a.length);
	}, [dadosGrafico1]);

	const highlightRegex = useMemo(() => {
		const escapedTerms = highlightTerms.map((term) =>
			term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
		);
		const patterns = [];
		if (escapedTerms.length) {
			patterns.push(`(?:${escapedTerms.join("|")})`);
		}
		patterns.push(
			"R\\$\\s?\\d{1,3}(?:\\.\\d{3})*(?:,\\d{2})?",
			"\\b\\d{4}-\\d{2}-\\d{2}\\b",
			"\\b\\d{2}\\/\\d{2}\\/\\d{4}\\b",
			"\\b\\d{2}-\\d{2}-\\d{4}\\b",
			"\\b20\\d{2}\\b",
			"[-+]?\\d+(?:[\\.,]\\d+)?\\s*%",
			"\\b\\d{1,3}(?:\\.\\d{3})+(?:,\\d{2})?\\b",
			"\\b\\d{2,}\\b",
		);
		return new RegExp(patterns.join("|"), "g");
	}, [highlightTerms]);

	const renderResumoTexto = useCallback(
		(text: string) => {
			if (!text) return text;
			const parts: Array<string | JSX.Element> = [];
			let lastIndex = 0;
			let matchIndex = 0;

			for (const match of text.matchAll(highlightRegex)) {
				const start = match.index ?? 0;
				const value = match[0];
				if (start > lastIndex) {
					parts.push(text.slice(lastIndex, start));
				}
				parts.push(
					<strong
						className="indicador-ia-resumo-highlight"
						key={`summary-highlight-${start}-${matchIndex}`}
					>
						{value}
					</strong>,
				);
				lastIndex = start + value.length;
				matchIndex += 1;
			}

			if (lastIndex < text.length) {
				parts.push(text.slice(lastIndex));
			}

			return parts.length ? parts : text;
		},
		[highlightRegex],
	);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<Layout title="Indicador Faturamento Mensal">
			<MotionContainer animationKey="IndicadorFaturamentoMensal" scroll>
				<div className="content-container-scrollable">
					<AnimatePresence mode="wait">
						{viewState === StateKey.RESULTS && (
							<>
								<Separator size="lg" />
								<div className="filtro-mes-ano">
									<div className="divergencias-sat-data-container">
										<FiltroData
											title="Início"
											value={datasPesquisa.inicio}
											onChange={(newValue) =>
												setDatasPesquisa((prev) => ({
													...prev,
													inicio: newValue,
												}))
											}
										/>
										{window.innerWidth > 445 && (
											<div className="filtro-data-divider" />
										)}
										<FiltroData
											title="Fim"
											titleFrente={window.innerWidth < 445}
											value={datasPesquisa.fim}
											onChange={(newValue) =>
												setDatasPesquisa((prev) => ({ ...prev, fim: newValue }))
											}
										/>
									</div>

									<div className="limpar-buttons-indicador">
										<Tooltip title="Pesquisar">
											<IconButton
												icon="Search"
												theme="dark"
												size="xl"
												borderColor="none"
												backgroundColor="#FFFFFF"
												hoverColor="#CCC"
												onClick={buscarDadosFiltrados}
											/>
										</Tooltip>
										<Tooltip title={<p>Limpar Filtro</p>}>
											<motion.div
												initial={{ rotate: 0 }}
												whileHover={{ rotate: 90 }}
												transition={{ duration: 0.2, ease: "easeInOut" }}
												className="select-field-chevron"
											>
												<IconButton
													disabled={isLoading}
													icon="X"
													size="xl"
													borderColor="none"
													onClick={handleClearSearch}
													backgroundColor="rgba(0, 0, 0, 0)"
													theme="dark"
												/>
											</motion.div>
										</Tooltip>
										<Tooltip title={resumoTooltip} disableInteractive>
											<div className="resumo-ia-button-wrapper">
												<Button
													label="Gerar resumo I.A"
													onClick={handleGerarResumoIa}
													loading={isSummaryLoading}
													disabled={isSummaryLoading || !isPesquisaAtual}
													size="sm"
													radius="md"
													icon="Sparkles"
													iconTheme="light"
													iconSize="sm"
												/>
											</div>
										</Tooltip>
									</div>
								</div>
								<Separator size="md" />
								{summaryState !== "idle" && (
									<>
										<div className="indicador-ia-resumo-card">
											<div className="indicador-ia-resumo-header">
												<div className="indicador-ia-resumo-title">
													<span className="indicador-ia-resumo-icon">
														<Icon name="Sparkles" size="sm" theme="green" />
													</span>
													<span>Resumo I.A</span>
												</div>
												<span className="indicador-ia-resumo-badge">I.A</span>
											</div>
											<span className="indicador-ia-resumo-periodo">
												Período:{" "}
												<strong className="indicador-ia-resumo-highlight">
													{datasPesquisa.inicio}
												</strong>{" "}
												a{" "}
												<strong className="indicador-ia-resumo-highlight">
													{datasPesquisa.fim}
												</strong>
											</span>
											{summaryState === "loading" && (
												<div className="indicador-ia-resumo-loading">
													<Loader size="md" />
													<span>Gerando resumo...</span>
												</div>
											)}
											{summaryState === "error" && (
												<div className="indicador-ia-resumo-error">
													<span>
														{summaryErrorMessage ||
															"Não foi possível gerar o resumo."}
													</span>
													<Button
														label="Tentar novamente"
														onClick={handleGerarResumoIa}
														disabled={isSummaryLoading}
														size="sm"
													/>
												</div>
											)}
											{summaryState === "success" && (
												<div className="indicador-ia-resumo-body">
													<p className="indicador-ia-resumo-text">
														{renderResumoTexto(summaryText)}
													</p>
													{summaryInsights.length > 0 && (
														<ul className="indicador-ia-resumo-list">
															{summaryInsights.map((insight, index) => (
																<li key={`${insight}-${index}`}>
																	{renderResumoTexto(insight)}
																</li>
															))}
														</ul>
													)}
												</div>
											)}
										</div>
										<Separator size="md" />
									</>
								)}
								<div className="tabela-container">
									<TabelaVirtualizadaGenerica
										dados={dadosGrafico1?.tabelaClientes || []}
										colunas={getColunasTabelaClientes}
										rowHeight={24}
										isLoading={isLoading}
										hasFooter={false}
										fullHeight
									/>
									<Separator size="lg" />
									<TabelaVirtualizadaGenerica
										dados={dadosGrafico1?.tabelaUnidades || []}
										colunas={getColunasTabelaUnidades(
											tabelaAnteriorLabel,
											tabelaAtualLabel,
										)}
										rowHeight={24}
										isLoading={isLoading}
										hasFooter={false}
										fullHeight
									/>
								</div>
								<div
									className="item-cock"
									style={{
										paddingLeft: "24px",
										paddingRight: "24px",
									}}
								>
									<ChartsHandler
										data={dadosGrafico1}
										type={"bar-stacked"}
										group={true}
										height="auto"
										width={570}
									/>
								</div>
								<Separator size="lg" />
								<TabelaVirtualizadaGenerica
									dados={dadosGrafico1?.series || []}
									colunas={colunasSeries}
									rowHeight={24}
									isLoading={isLoading}
									columnWidthsOverride
									hasFooter={false}
									fullHeight
								/>
							</>
						)}
						{viewState === StateKey.LOADING && (
							<LoadingComponent key="IndicadorFaturamentoMensalLoading" />
						)}
					</AnimatePresence>
				</div>
			</MotionContainer>
			<Separator size="lg" />
		</Layout>
	);
}

export default memo(IndicadorFaturamentoMensal);
